import { FC, ReactNode } from 'react'
import { SvgIconProps, Box, Theme, Typography, useTheme, useMediaQuery, Button, SxProps } from '@mui/material'
import { useTranslation } from 'react-i18next'

type Props = {
  title: string
  description: string
  /**
   * description when initial description is not found
   */
  fallbackDescription?: string

  /**
   *
   * Overruled when isMobile :) then its allways 'vertical'
   * @default 'vertical'
   */
  direction?: 'horizontal' | 'vertical'

  dataTestId?: string

  /**
   * Show it in fullheight
   * @default false
   */
  isFullHeight?: boolean

  graphic: FC<SvgIconProps>

  sx?: SxProps<Theme>

  /**
   * @deprecated: using standard for gap, or making it optional
   */
  contentSx?: SxProps<Theme>

  cta?: {
    /**
     * i18n labelsheet key
     * @example app.Pay.retry
     *
     * @default 'Button' not even from i18n :(
     */
    title?: string

    /**
     * Optional icon
     */
    icon?: ReactNode

    dataTestId?: string

    /**
     * When you have a call to action you should have a click callback handler :)
     */
    onClick: () => void
  }

  /**
   * When you need to pass variables to your labels
   * these variables will be passed to the title, description and cta.title
   */
  i18nOptions?: {}
}

const styles: Record<string, SxProps<Theme>> = {
  vertical: {
    flexDirection: 'column',
    justifyContent: 'center',
    '& .infographic-text-primary': {
      textAlign: 'center',
    },
    '& .infographic-button-container': {
      justifyContent: 'center',
    },
  },
  horizontal: {
    flexDirection: 'row',
    '& .infographic-button-container': {
      justifyContent: 'start',
    },
  },
  svg: {
    maxWidth: '80vw',
    width: '300px',
    height: '150px',
  },
}

export const InfoGraphic: FC<Props> = ({
  // TODO: Make eslint less strict?
  // eslint-disable-next-line @typescript-eslint/naming-convention
  graphic: Graphic,
  title,
  description,
  fallbackDescription = '',
  dataTestId,
  cta,
  direction = 'vertical',
  isFullHeight = false,
  sx,
  contentSx,
  i18nOptions,
}) => {
  const { t } = useTranslation()
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('md'))
  const displayDirection = isMobile ? 'vertical' : direction

  return (
    <Box
      display="flex"
      flex={1}
      alignItems="center"
      height={isFullHeight ? '100%' : undefined}
      data-testid={dataTestId}
      sx={
        {
          ...(sx || {}),
          ...(displayDirection === 'horizontal' ? styles.horizontal : styles.vertical),
        } as SxProps<Theme>
      }
    >
      <Box display="flex" flexDirection="column" height="inherit">
        <Box alignItems="center" display="flex" flex={1} flexDirection="column" justifyContent="center" sx={contentSx}>
          <Graphic sx={styles.svg} />

          <Typography gutterBottom className="infographic-text-primary" color="grey.700" mt={1} variant="h4">
            {
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              t(title as unknown as any, i18nOptions)
            }
          </Typography>
          <Typography gutterBottom className="infographic-text-secondary" color="grey.700" align="center">
            {
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              t([description as unknown as any, fallbackDescription], i18nOptions)
            }
          </Typography>
        </Box>

        {cta && (
          <Box className="infographic-button-container" display="flex" mb={5} mt="auto" pt={2} width="100%">
            <Button
              onClick={cta.onClick}
              variant="contained"
              color="primary"
              fullWidth={isMobile}
              startIcon={cta.icon}
              data-testid={cta.dataTestId}
            >
              {
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                t((cta.title || 'Button') as unknown as any, i18nOptions)
              }
            </Button>
          </Box>
        )}
      </Box>
    </Box>
  )
}
